import { AreaChart, ChartTooltip, LineChart as MantineLineChart } from '@mantine/charts';
import { Button, Card, Center, Divider, Grid, Modal, Stack, Text, Title } from '@mantine/core';
import Colors from '../../constants/Colors';
import { IconArrowsMaximize, IconChartPieOff } from '@tabler/icons-react';
import { cloneElement, isValidElement, useState } from 'react';

const LineChart = ({ data, currency, children, title, subTitle, stacked, expand, type }) => {
  const [expandChartModalOpen, setExpandChartModalOpen] = useState(false);

  // Check if data exists and has at least one series with actual data points
  const hasData =
    data && Array.isArray(data) && data.length > 0 && data.some((series) => series.data && series.data.length > 0);
  const hasChildren = children && (Array.isArray(children) ? children.length > 0 : Boolean(children));

  // Check if we have the data array structure (even if all series are empty)
  const hasDataStructure = data && Array.isArray(data) && data.length > 0;

  // If no data structure at all and no children, render nothing
  if (!hasDataStructure && !hasChildren) {
    return null;
  }

  // Deduplicate series by id - if there are duplicates, keep only the first one
  const deduplicatedData = hasData
    ? data.filter((series, index, self) => self.findIndex((s) => s.id === series.id) === index)
    : [];

  // Sanitize series id for use as object key (Recharts uses dot notation for nested access)
  const sanitizeKey = (id) => id.replace(/\./g, '_dot_');

  // Transform Nivo data format to Mantine/Recharts format
  // Nivo format: [{ id: 'series1', data: [{ x: 'A', y: 10 }, ...] }, ...]
  // Mantine format: [{ date: 'A', series1: 10, ... }, ...]
  const transformData = () => {
    if (!hasData) return [];

    // Get all unique x values across all series
    const xValues = new Set();
    deduplicatedData.forEach((series) => {
      series.data.forEach((point) => {
        xValues.add(point.x);
      });
    });

    // Create transformed data array - use sanitized keys
    return Array.from(xValues).map((x) => {
      const point = { date: x };
      deduplicatedData.forEach((series) => {
        const dataPoint = series.data.find((p) => p.x === x);
        point[sanitizeKey(series.id)] = dataPoint ? dataPoint.y : 0;
      });
      return point;
    });
  };

  const transformedData = transformData();

  // Build series configuration - use sanitized dataKey but original name for display
  // Supports optional color property in data, falls back to Colors palette
  const series = hasData
    ? deduplicatedData.map((s, i) => ({
        name: s.id,
        dataKey: sanitizeKey(s.id),
        color: s.color || Colors[i % Colors.length],
      }))
    : [];

  // Abbreviate large numbers (e.g., 1200000 -> 1.2M)
  const abbreviateNumber = (value) => {
    if (value === undefined || value === null) return '';
    const abs = Math.abs(value);
    if (abs >= 1_000_000_000) {
      return (value / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
    }
    if (abs >= 1_000_000) {
      return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (abs >= 1_000) {
      return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return value.toLocaleString(navigator.language);
  };

  // Custom value formatter for currency
  const valueFormatter = currency
    ? (value) => (value === undefined || value === null ? '' : value.toLocaleString(navigator.language, { style: 'currency', currency }))
    : (value) => (value === undefined || value === null ? '' : value.toLocaleString(navigator.language));

  // Compact formatter for Y-axis labels (uses abbreviations for large numbers)
  const yAxisFormatter = currency
    ? (value) => {
        if (value === undefined || value === null) return '';
        return new Intl.NumberFormat(navigator.language, {
          style: 'currency',
          currency,
          notation: 'compact',
          maximumFractionDigits: 1,
        }).format(value);
      }
    : abbreviateNumber;

  // Format X-axis labels: strip year from YYYY-MM-DD format
  const xAxisFormatter = (value) => {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value.slice(5); // Remove "YYYY-" prefix, keep "MM-DD"
    }
    return value;
  };

  const ChartComponent = stacked ? AreaChart : MantineLineChart;

  const height = expand ? 150 : 250;

  // Create chart with configurable props based on available space
  const createChart = (isModal = false) => {
    const isCompact = expand && !isModal;
    const chartHeight = isModal ? 350 : height;
    return (
      <div style={{ minHeight: chartHeight, minWidth: 100 }}>
        <ChartComponent
          h={chartHeight}
          data={transformedData}
          dataKey="date"
          series={series}
          curveType="monotone"
          withLegend={!isCompact}
          legendProps={{
            layout: 'horizontal',
            verticalAlign: 'top',
            align: 'right',
            wrapperStyle: { display: 'flex', flexDirection: 'row', gap: '16px' },
            formatter: (value) => <span style={{ marginLeft: '4px' }}>{value}</span>,
          }}
          valueFormatter={valueFormatter}
          tickLine="y"
          gridAxis="y"
          withDots={!isCompact}
          dotProps={{ r: 2 }}
          withXAxis={true}
          xAxisProps={{ tickFormatter: xAxisFormatter }}
          withYAxis={true}
          yAxisProps={{ tickFormatter: yAxisFormatter }}
          tooltipAnimationDuration={200}
          tooltipProps={{
            content: ({ label, payload }) => {
              if (!payload || payload.length === 0) return null;
              const sortedPayload = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));
              return <ChartTooltip label={label} payload={sortedPayload} valueFormatter={valueFormatter} />;
            },
          }}
          {...(stacked ? { type: 'stacked' } : {})}
        />
      </div>
    );
  };

  const noDataPlaceholder = (
    <Center h="100%">
      <Stack align="center" gap="xs">
        <IconChartPieOff size={48} color="var(--mantine-color-dimmed)" stroke={1.5} />
        <Text c="dimmed" size="sm">
          No Data
        </Text>
      </Stack>
    </Center>
  );

  const lineChart = hasData ? createChart(false) : noDataPlaceholder;

  const modalChart = hasData ? (
    <Grid.Col span={12} style={{ minHeight: 350 }}>
      {createChart(true)}
    </Grid.Col>
  ) : null;

  // Build content based on what's available
  // Pass smaller size to PieChart when in compact mode
  const pieChartSize = expand ? 120 : 160;
  const childrenWithSize = isValidElement(children) ? cloneElement(children, { size: pieChartSize }) : children;

  let content;
  if (hasChildren) {
    content = (
      <>
        <Grid.Col span={{ base: 4, lg: 3 }} style={{ minHeight: height }}>
          {childrenWithSize}
        </Grid.Col>
        <Grid.Col span={{ base: 8, lg: 9 }} style={{ minHeight: height }}>
          {lineChart}
        </Grid.Col>
      </>
    );
  } else {
    content = (
      <Grid.Col span={12} style={{ minHeight: height }}>
        {lineChart}
      </Grid.Col>
    );
  }
  const expandButton =
    expand && hasData ? (
      <Button size="sm" variant="subtle" onClick={() => setExpandChartModalOpen(true)}>
        <IconArrowsMaximize size={16} />
      </Button>
    ) : null;

  const chartContent = (
    <>
      {title ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ flex: 1 }} />
          <Title order={4} style={{ flex: 'none' }}>
            {title}
          </Title>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>{expandButton}</div>
        </div>
      ) : expandButton ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{expandButton}</div>
      ) : null}
      {title ? <Divider my="sm" /> : null}
      <Grid>{content}</Grid>
      {subTitle ? (
        <>
          <Divider my="sm" />
          <Title order={5} c="dimmed" ta="center">
            {subTitle}
          </Title>
        </>
      ) : null}
    </>
  );

  return (
    <>
      {type === 'card' ? (
        <Card padding="md" withBorder radius="md">
          {chartContent}
        </Card>
      ) : (
        <div>{chartContent}</div>
      )}
      <br />
      <Modal size="xl" opened={expandChartModalOpen} onClose={() => setExpandChartModalOpen(false)} title={title} keepMounted={false}>
        <Grid>{modalChart}</Grid>
        <Grid justify="flex-end" mt="md">
          <Grid.Col span="content">
            <Button variant="default" onClick={() => setExpandChartModalOpen(false)}>
              Close
            </Button>
          </Grid.Col>
        </Grid>
      </Modal>
    </>
  );
};

export default LineChart;
